import React, { useState } from 'react';

interface BookingCalendarProps {
  onDateSelect: (startDate: Date, endDate: Date, startTime: string, endTime: string) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const BookingCalendar: React.FC<BookingCalendarProps> = ({ 
  onDateSelect, 
  onCancel, 
  isOpen 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('9:00');
  const [endTime, setEndTime] = useState('9:00');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [useCustomPickupTime, setUseCustomPickupTime] = useState(false);
  const [useCustomReturnTime, setUseCustomReturnTime] = useState(false);

  const popularTimes = ['9:00', '11:00', '12:00', '14:30', '17:30'];

  // Helper function to check if a time is available for today
  const isTimeAvailable = (time: string, forDate: Date | null) => {
    if (!forDate) return true;
    
    const today = new Date();
    const selectedDate = new Date(forDate);
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    // If not today, all times are available
    if (selectedDate.getTime() !== today.getTime()) return true;
    
    // For today, check if the time has passed (no buffer)
    const currentTime = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    const timeToCheck = new Date();
    timeToCheck.setHours(hours, minutes, 0, 0);
    
    // Only block times that have already passed
    return timeToCheck > currentTime;
  };

  // Helper function to get minimum time for today
  const getMinTimeForToday = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday = 0

    const days = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(year, month, 0); // Last day of previous month
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: prevMonthDays - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, prevMonthDays - i)
      });
    }
    
    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: day,
        isCurrentMonth: true,
        fullDate: new Date(year, month, day)
      });
    }
    
    // Next month's leading days
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: day,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, day)
      });
    }
    
    return days;
  };

  const handleDateClick = (date: Date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    // Allow selecting any date (past, present, or future)
    
    if (!selectedStartDate || isSelectingEnd) {
      if (!selectedStartDate) {
        setSelectedStartDate(date);
        setIsSelectingEnd(true);
      } else if (date >= selectedStartDate) {
        setSelectedEndDate(date);
        setIsSelectingEnd(false);
      } else {
        // If selected date is before start date, reset
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setIsSelectingEnd(true);
      }
    } else {
      // If clicking the same date as start date, make it a same-day rental
      if (selectedStartDate && date.getTime() === selectedStartDate.getTime()) {
        setSelectedEndDate(date);
        setIsSelectingEnd(false);
      } else {
        // Reset selection with new start date
        setSelectedStartDate(date);
        setSelectedEndDate(null);
        setIsSelectingEnd(true);
      }
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  const isDateInRange = (date: Date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date: Date) => {
    if (!selectedStartDate) return false;
    if (selectedStartDate.getTime() === date.getTime()) return true;
    if (selectedEndDate && selectedEndDate.getTime() === date.getTime()) return true;
    return false;
  };

  const handleApply = () => {
    if (selectedStartDate && selectedEndDate) {
      const finalStartTime = useCustomPickupTime ? customStartTime : startTime;
      const finalEndTime = useCustomReturnTime ? customEndTime : endTime;
      
      // Only validate times for today (allow any time for past/future dates)
      const isStartToday = new Date().toDateString() === selectedStartDate.toDateString();
      const isEndToday = new Date().toDateString() === selectedEndDate.toDateString();
      
      if (useCustomPickupTime && isStartToday && customStartTime) {
        if (!isTimeAvailable(customStartTime, selectedStartDate)) {
          alert('Pickup time cannot be in the past for today.');
          return;
        }
      }
      
      if (useCustomReturnTime && isEndToday && customEndTime) {
        if (!isTimeAvailable(customEndTime, selectedEndDate)) {
          alert('Return time cannot be in the past for today.');
          return;
        }
      }
      
      onDateSelect(selectedStartDate, selectedEndDate, finalStartTime, finalEndTime);
    }
  };

  const handleCancel = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setIsSelectingEnd(false);
    setUseCustomPickupTime(false);
    setUseCustomReturnTime(false);
    setCustomStartTime('');
    setCustomEndTime('');
    setStartTime('9:00');
    setEndTime('9:00');
    onCancel();
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-white">
          <h2 className="text-xl font-semibold">Select Rental Period</h2>
          <p className="text-primary-100 text-sm mt-1">
            {selectedStartDate && selectedEndDate 
              ? `${selectedStartDate.toLocaleDateString()} - ${selectedEndDate.toLocaleDateString()}`
              : isSelectingEnd 
                ? 'Select return date (same day allowed)'
                : 'Select pickup date (any date allowed)'
            }
          </p>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Calendar Section */}
          <div className="flex-1 p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <h3 className="text-lg font-semibold">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const isToday = new Date().toDateString() === day.fullDate.toDateString();
                const isSelected = isDateSelected(day.fullDate);
                const isInRange = isDateInRange(day.fullDate);
                
                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(day.fullDate)}
                    className={`
                      aspect-square flex items-center justify-center text-sm rounded-lg transition-all hover:bg-primary-50
                      ${!day.isCurrentMonth ? 'text-gray-300' : ''}
                      ${isToday ? 'ring-2 ring-primary-500 ring-offset-1' : ''}
                      ${isSelected ? 'bg-primary-500 text-white font-semibold' : ''}
                      ${isInRange && !isSelected ? 'bg-primary-100 text-primary-700' : ''}
                    `}
                  >
                    {day.date}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Selection Section */}
          <div className="w-72 bg-gray-50 p-6 border-l overflow-y-auto">
            <div className="space-y-6">
              {/* Pickup Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Pickup Time
                </label>
                <div className="space-y-2">
                  {/* Popular Times */}
                  {popularTimes.map(time => {
                    const timeAvailable = isTimeAvailable(time, selectedStartDate);
                    return (
                      <button
                        key={`pickup-${time}`}
                        onClick={() => {
                          if (timeAvailable) {
                            setStartTime(time);
                            setUseCustomPickupTime(false);
                          }
                        }}
                        disabled={!timeAvailable}
                        className={`w-full p-2 text-sm rounded-lg border transition-colors ${
                          !timeAvailable 
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : startTime === time && !useCustomPickupTime
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {time} {!timeAvailable ? '(Past)' : ''}
                      </button>
                    );
                  })}
                  
                  {/* Custom Time Option */}
                  <div className="border-t pt-2">
                    <button
                      onClick={() => setUseCustomPickupTime(!useCustomPickupTime)}
                      className={`w-full p-2 text-sm rounded-lg border transition-colors ${
                        useCustomPickupTime
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Custom Time
                    </button>
                    
                    {useCustomPickupTime && (() => {
                      const isToday = selectedStartDate && new Date().toDateString() === selectedStartDate.toDateString();
                      const minTime = isToday ? getMinTimeForToday() : undefined;
                      
                      return (
                        <div className="mt-2">
                          <input
                            type="time"
                            value={customStartTime}
                            onChange={(e) => setCustomStartTime(e.target.value)}
                            min={minTime}
                            className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            placeholder="Select time"
                          />
                          {isToday ? (
                            <p className="text-xs text-gray-500 mt-1">
                              Minimum time: {minTime} (current time for today)
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-1">
                              Any time allowed for this date
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Return Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Return Time
                </label>
                <div className="space-y-2">
                  {/* Popular Times */}
                  {popularTimes.map(time => {
                    const timeAvailable = isTimeAvailable(time, selectedEndDate);
                    return (
                      <button
                        key={`return-${time}`}
                        onClick={() => {
                          if (timeAvailable) {
                            setEndTime(time);
                            setUseCustomReturnTime(false);
                          }
                        }}
                        disabled={!timeAvailable}
                        className={`w-full p-2 text-sm rounded-lg border transition-colors ${
                          !timeAvailable 
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : endTime === time && !useCustomReturnTime
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {time} {!timeAvailable ? '(Past)' : ''}
                      </button>
                    );
                  })}
                  
                  {/* Custom Time Option */}
                  <div className="border-t pt-2">
                    <button
                      onClick={() => setUseCustomReturnTime(!useCustomReturnTime)}
                      className={`w-full p-2 text-sm rounded-lg border transition-colors ${
                        useCustomReturnTime
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Custom Time
                    </button>
                    
                    {useCustomReturnTime && (() => {
                      const isToday = selectedEndDate && new Date().toDateString() === selectedEndDate.toDateString();
                      const minTime = isToday ? getMinTimeForToday() : undefined;
                      
                      return (
                        <div className="mt-2">
                          <input
                            type="time"
                            value={customEndTime}
                            onChange={(e) => setCustomEndTime(e.target.value)}
                            min={minTime}
                            className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            placeholder="Select time"
                          />
                          {isToday ? (
                            <p className="text-xs text-gray-500 mt-1">
                              Minimum time: {minTime} (current time for today)
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-1">
                              Any time allowed for this date
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Rental Summary */}
              {selectedStartDate && selectedEndDate && (() => {
                const durationDays = Math.max(1, Math.ceil((selectedEndDate.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24)));
                const isSameDay = selectedStartDate.getTime() === selectedEndDate.getTime();
                
                return (
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-900 mb-2">Rental Period</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>From: {selectedStartDate.toLocaleDateString()} at {useCustomPickupTime ? customStartTime || 'Select time' : startTime}</p>
                      <p>To: {selectedEndDate.toLocaleDateString()} at {useCustomReturnTime ? customEndTime || 'Select time' : endTime}</p>
                      <p className="font-medium text-primary-600 mt-2">
                        Duration: {durationDays} day{durationDays === 1 ? '' : 's'}{isSameDay ? ' (Same day rental)' : ''}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t flex-shrink-0">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleApply}
            disabled={
              !selectedStartDate || 
              !selectedEndDate || 
              (useCustomPickupTime && !customStartTime) ||
              (useCustomReturnTime && !customEndTime)
            }
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
